import { AIPrefillBadge } from '@components/AIPrefillBadge'
import { FocusVisibleRadio } from '@components/FocusVisible'
import {
	EMPTY_PREFILL_PROPS,
	type PrefillableFieldProps,
} from '@hooks/usePrefillableFieldProps'
import {
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Radio,
	RadioGroup,
	type RadioGroupProps,
	Stack,
} from '@mui/material'
import { type ReactNode, useCallback } from 'react'
import {
	type Control,
	Controller,
	type ControllerFieldState,
	type ControllerRenderProps,
	type FieldPath,
	type FieldPathValue,
	type FieldValues,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props<
	TFieldValues extends FieldValues,
	TName extends FieldPath<TFieldValues>,
> {
	control: Control<TFieldValues>
	name: TName
	isRequired?: boolean
	legend?: ReactNode
	/**
	 * Adornment rendered as a sibling of the legend (after the asterisk) — e.g. a
	 * tooltip icon. Mirrors BooleanFieldController so the required asterisk stays
	 * next to the label text rather than being pushed past the icon.
	 */
	labelAdornment?: ReactNode
	options: readonly {
		label: string
		value: FieldPathValue<TFieldValues, TName>
	}[]
	prefillProps?: PrefillableFieldProps
	onChange?: (value: FieldPathValue<TFieldValues, TName>) => void
	externalError?: string
}

const REQUIRED_RULES = { required: true }
const FORM_CONTROL_SX = { padding: 0 }

const RADIO = <Radio />

const WithLegend = ({
	children,
	legend,
	labelAdornment,
	showLegendInHeader = false,
	error = false,
	isRequired = false,
}: {
	children: React.ReactNode
	legend?: ReactNode
	labelAdornment?: ReactNode
	showLegendInHeader?: boolean
	error?: boolean
	isRequired?: boolean
}) =>
	legend && !showLegendInHeader ? (
		<FormControl component="fieldset" sx={FORM_CONTROL_SX} error={error}>
			<Stack direction="row" alignItems="center" spacing={1}>
				<FormLabel component="legend">
					{legend}
					{isRequired && <span aria-hidden="true">{' *'}</span>}
				</FormLabel>
				{labelAdornment}
			</Stack>
			{children}
		</FormControl>
	) : (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>{children}</>
	)

const RadioGroupWithExtraArgOnChange = <T,>({
	onChange,
	extraArgOnChange,
	...props
}: Omit<RadioGroupProps, 'onChange'> & {
	extraArgOnChange: T
	onChange: (e: React.ChangeEvent<HTMLInputElement>, extraArg: T) => void
}) => {
	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => onChange(e, extraArgOnChange),
		[onChange, extraArgOnChange]
	)
	return <RadioGroup {...props} onChange={handleChange} />
}
const RadioGroupController = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	control,
	name,
	isRequired,
	legend,
	labelAdornment,
	options,
	prefillProps: { isPrefilled, onFieldChange } = EMPTY_PREFILL_PROPS,
	onChange: onChangeCallback,
	externalError,
}: Props<TFieldValues, TName>) => {
	const { t } = useTranslation()

	const handleChange = useCallback(
		(
			e: React.ChangeEvent<HTMLInputElement>,
			onChange: (value: FieldPathValue<TFieldValues, TName>) => void
		) => {
			const newValue = e.target.value as FieldPathValue<TFieldValues, TName>

			if (onFieldChange) {
				onFieldChange(newValue, onChange)
			} else {
				onChange(newValue)
			}

			if (onChangeCallback) {
				onChangeCallback(newValue)
			}
		},
		[onChangeCallback, onFieldChange]
	)

	const renderField = useCallback(
		({
			field,
			fieldState: { error },
		}: {
			field: ControllerRenderProps<TFieldValues, TName>
			fieldState: ControllerFieldState
		}) => (
			<WithLegend
				legend={legend}
				labelAdornment={labelAdornment}
				showLegendInHeader={isPrefilled}
				error={!!error || !!externalError}
				isRequired={isRequired}
			>
				{isPrefilled && legend && (
					<Stack gap={1} mb={1}>
						<Stack direction="row" alignItems="center" spacing={1}>
							<FormLabel component="span" sx={{ m: 0 }}>
								{legend}
							</FormLabel>
							<AIPrefillBadge />
						</Stack>
					</Stack>
				)}
				<RadioGroupWithExtraArgOnChange
					value={field.value ?? ''}
					row
					onChange={handleChange}
					extraArgOnChange={field.onChange}
				>
					{options.map(({ label, value }, index) => (
						<FormControlLabel
							key={value}
							value={value}
							control={index ? RADIO : <FocusVisibleRadio ref={field.ref} />}
							label={label}
						/>
					))}
				</RadioGroupWithExtraArgOnChange>
				<FormHelperText error={!!error || !!externalError} sx={{ ml: 0 }}>
					{error?.type === 'required'
						? t('input.required')
						: (error?.message ?? externalError)}
				</FormHelperText>
			</WithLegend>
		),
		[
			externalError,
			handleChange,
			isPrefilled,
			isRequired,
			labelAdornment,
			legend,
			options,
			t,
		]
	)

	return (
		<Controller
			// JsonForms reconciles renderers across page transitions, so the same Controller
			// fiber would otherwise see its `name` prop change. RHF's useController then fires
			// unregister(previousName) on the name-change branch, wiping the previous field
			// from both _formValues and _defaultValues — so on return the radio reads the
			// stale initial default instead of the supplier's recent click. Keying by name
			// forces a fresh Controller instance per field (SUPE-2146).
			// TODO(larryhudson): drop the RHF Controller from this renderer entirely
			// (SUPE-2169). JsonForms data should be the single source of truth; this `key`
			// workaround is a Band-Aid for the dual-store sync problem the cleanup ticket
			// addresses.
			key={name}
			name={name}
			control={control}
			rules={isRequired ? REQUIRED_RULES : undefined}
			render={renderField}
		/>
	)
}

export { RadioGroupController }
